import { api } from 'src/services/api/axios';
import { Address, AddressResponse } from '../types';

const API_BASE = '/address';
const mapApiResponseToAddressList = (addresses: any[]): Address[] => {
  if (!Array.isArray(addresses)) return [];
  return addresses.map((item: any, index: number) => ({
    id: item.id || `address-${index}`,
    title: item.title || `Address ${index + 1}`,
    firstName: item.first_name || item.firstName || '',
    lastName: item.last_name || item.lastName || '',
    phoneCode: item.phone_code || item.phoneCode || '+66',
    phoneNumber: item.phone || '',
    country: item.country || '',
    address: item.address_1 || item.address || '',
    province: item.city || item.province || '',
    district: item.state || item.district || '',
    postcode: item.postcode || '',
    isDefault: item.is_default || item.isDefault || false
  }));
};

const authenticationHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
});

export const addressService = {
  getAddresses: async (): Promise<{ data: Address[] }> => {
    try {
      const response = await api.get(`${API_BASE}/all`, {
        headers: authenticationHeaders()
      });
      const addresses = mapApiResponseToAddressList(response.data?.data?.addresses || []);
      return { data: addresses };
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(apiMessage || error.message);
    }
  },

  createAddress: async (address: Address): Promise<AddressResponse> => {
    try {
      const createData = {
        title: address.title,
        first_name: address.firstName,
        last_name: address.lastName,
        phone_code: address.phoneCode,
        phone: address.phoneNumber,
        country: address.country,
        address_1: address.address,
        city: address.province,
        state: address.district,
        postcode: address.postcode,
        district: address.district,
        province: address.province,
        is_default: address.isDefault
      };
      const response = await api.post(`${API_BASE}/add`, createData, {
        headers: authenticationHeaders()
      });
      return response.data;
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(apiMessage || error.message);
    }
  },

  updateAddress: async (id: string, address: Partial<Address>): Promise<AddressResponse> => {
    try {
      const updateData = {
        title: address.title,
        address_id: id,
        first_name: address.firstName,
        last_name: address.lastName,
        phone_code: address.phoneCode,
        phone: address.phoneNumber,
        country: address.country,
        address_1: address.address,
        city: address.province,
        state: address.district,
        postcode: address.postcode,
        district: address.district,
        province: address.province,
        is_default: address.isDefault
      };
      const response = await api.put<AddressResponse>(`${API_BASE}/update`, updateData, {
        headers: authenticationHeaders()
      });
      return response.data;
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(apiMessage || error.message);
    }
  },

  deleteAddress: async (id: string): Promise<AddressResponse> => {
    try {
      const response = await api.delete<AddressResponse>(`${API_BASE}/delete`, {
        headers: authenticationHeaders(),
        data: { address_id: id }
      });
      return response.data;
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(apiMessage || error.message);
    }
  },

  setDefaultAddress: async (id: string): Promise<AddressResponse> => {
    try {
      const response = await api.post<AddressResponse>(`${API_BASE}/default`, { address_id: id }, {
        headers: authenticationHeaders()
      });
      return response.data;
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      throw new Error(apiMessage || error.message);
    }
  },
};
